// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
// to do, and the count of files carrying it is the size of the refactor.
import axios from 'axios';
import { UnifiedAuthStorage } from './unified-auth.service';
import ENV from '../config/legacy-env.js';
import ENDPOINTS from '../config/endpoints.js';
import { logger } from '@/shared/observability/logger';

/**
 * API Response for theme update
 */
export interface ThemeUpdateResponse {
  success: boolean;
  message?: string;
  data?: {
    userId?: string;
    theme?: string;
  };
  error?: string;
}

/**
 * Theme Service - Handles theme persistence on backend
 */
export class ThemeService {
  /**
   * Get auth headers for API requests
   */
  private static getAuthHeaders() {
    const token = UnifiedAuthStorage.getToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Update user theme preference on backend
   * 
   * @param userId - User ID
   * @param theme - Theme name (e.g., "light", "dark", "auto", "light-glass", "dark-glass", etc.)
   * @returns Response from backend
   */
  static async updateTheme(userId: string, theme: string): Promise<ThemeUpdateResponse> {
    try {
      const token = UnifiedAuthStorage.getToken();
      if (!token) {
        return {
          success: false,
          error: 'Authentication required. Please login again.',
        };
      }

      // Backend expects POST for preferences/theme per API contract.
      // (Postman tests showed 200 OK with POST; PUT resulted in 500/405 mismatch.)
      const response = await axios.post(
        `${ENV.API_BASE_URL}${ENDPOINTS.USER.THEME}`,
        {
          userId,
          theme
        },
        {
          headers: this.getAuthHeaders()
        }
      );

      return {
        success: true,
        message: 'Theme updated successfully',
        data: response.data,
      };
    } catch (error: any) {
      logger.error('❌ Theme update failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || 
               error.response?.data?.error || 
               error.message || 
               'Failed to update theme'
      };
    }
  }

  /**
   * Get user's theme preference from backend
   * Note: In practice, this is already returned during login via UnifiedAuthStorage
   * This method is provided for explicit refresh if needed
   * 
   * @returns Current theme from user data
   */
  static getUserTheme(): string | null {
    const userInfoTheme = UnifiedAuthStorage.getUserInfo()?.theme;
    const userTheme = UnifiedAuthStorage.getUser()?.theme;
    return userInfoTheme || userTheme || null;
  }

  /**
   * Validate if a theme name is valid
   * Themes are dynamic with no fixed enum, so validation is minimal
   * 
   * @param theme - Theme name to validate
   * @returns true if theme appears valid (non-empty string)
   */
  static isValidTheme(theme: any): boolean {
    return typeof theme === 'string' && theme.trim().length > 0;
  }
}

export default ThemeService;
